import { COMPLAINT_STATUS, LANGUAGE_CODE_MAPPER, LANGUAGE_EN, LOGIN_METHODS, ROUTES, USER_LOGIN_TYPES, USER_ROLES, USER_TYPES, WAREHOUSE_ITEM_ACTION } from "../constants";
import chroma from "chroma-js";
import { countryCodeToCountryName, equalAsStringOrNumber } from "../utils";
import { getSelectableFontsForBrand, isDevOrLocalEnv } from "rylak-common";
import _ from "lodash";
import { getComplaintsPageData, getRouteHref, getUploadShipmentLabelPageData, getWarehouseRemarksPageData } from "./pageAccessCore";
import { LANGUAGE_EN_TR } from "rylak-common/src/constants";

// NOTE: Can not import any React code

export function getUserRole(appProps) {
    return appProps?.userData?.userRole;
}

function getUserType(appProps) {
    return appProps?.userData?.userType;
}

function getUserLoginType(appProps) {
    return appProps?.userData?.userLoginType;
}

export function isRoleSuperAdmin(appProps) {
    return getUserRole(appProps) === USER_ROLES.SUPER_ADMIN.id;
}

export function isRoleAdmin(appProps) {
    return getUserRole(appProps) === USER_ROLES.ADMIN.id;
}

export function isLoginTypeShopify(appProps) {
    return getUserLoginType(appProps) === USER_LOGIN_TYPES.SHOPIFY;
}

export function isRoleSupport(appProps) {
    return getUserRole(appProps) === USER_ROLES.SUPPORT.id;
}

export function isRoleObserver(appProps) {
    return getUserRole(appProps) === USER_ROLES.OBSERVER.id;
}

export function isRoleShipment(appProps) {
    return getUserRole(appProps) === USER_ROLES.SHIPMENT.id;
}

export function isRoleWarehouse(appProps) {
    return getUserRole(appProps) === USER_ROLES.WAREHOUSE.id;
}

export function isRoleAdminOrGreater(appProps) {
    return isRoleSuperAdmin(appProps) || isRoleAdmin(appProps) || isRoleObserver(appProps);
}

export function isRoleSupportOrGreater(appProps) {
    return isRoleAdminOrGreater(appProps) || isRoleObserver(appProps) || isRoleSupport(appProps);
}

export function isUserTypeIngrid(appProps) {
    return getUserType(appProps) === USER_TYPES.TURNR;
}

export function isUserTypeShop(appProps) {
    return getUserType(appProps) === USER_TYPES.SHOP;
}

export function isUserTypeShopGroup(appProps) {
    return getUserType(appProps) === USER_TYPES.SHOP_GROUP;
}

export function isUserTypeShopGroupOrGreater(appProps) {
    return isUserTypeIngrid(appProps) || isUserTypeShopGroup(appProps);
}

export function isUserTypeShopShipmentCarrier(appProps) {
    return getUserType(appProps) === USER_TYPES.SHIPMENT_CARRIER;
}

export function isUserTypeShopOrGreater(appProps) {
    return isUserTypeIngrid(appProps) || isUserTypeShopGroup(appProps) || isUserTypeShop(appProps);
}

export function isStartupGuideActive() {
    return false; //brandService?.data?.showGuide === true;
}

export function isColorVeryBright(color) {
    return chroma(color).luminance() > 0.5;
}

export function getSelectedBrandName(appProps) {
    return appProps.selectedBrandName;
}

export function getSelectedBrandDisplayName(appProps) {
    return getSelectedBrandData(appProps)?.displayName || getSelectedBrandName(appProps);
}

export function getBrandReturnAppLiveBaseUrl(appProps) {
    return `${appProps?.system?.returnsBaseUrl}`;
}

export function getBrandReturnAppLiveUrl(appProps) {
    return `${getBrandReturnAppLiveBaseUrl(appProps)}/${getSelectedBrandName(appProps)}`;
}

export function getBrandReturnAppDemoBaseUrl(appProps) {
    return `${appProps?.system?.demoBaseUrl}`;
}

export function getBrandReturnAppDemoUrl(appProps, { iframeMode, path = "" } = {}) {
    const baseUrlSplitOnQuery = getBrandReturnAppDemoBaseUrl(appProps).split("?");
    let url = `${baseUrlSplitOnQuery[0]}/${getSelectedBrandName(appProps)}${path}?${baseUrlSplitOnQuery[1] || ""}`;
    url = iframeMode === true || iframeMode === false ? url + "&iframe=" + iframeMode : url;
    url += `&real_return_reasons=true`;
    if (url.charAt(url.length - 1) === "?") {
        url = url.slice(0, -1);
    }
    return url;
}

export function getAllLanguageCodes() {
    return _.keys(LANGUAGE_CODE_MAPPER).filter((code) => code !== LANGUAGE_EN_TR);
}

export function getSelectedBrandEnabledLanguages(appProps) {
    const brand = getSelectedBrandData(appProps);
    const enabledLanguages = brand?.settings?.enabledLanguages || [];
    return enabledLanguages?.length ? enabledLanguages : getAllLanguageCodes(appProps);
}

export function getSelectedBrandEnabledLanguagesIncludingEnglishAISorted(appProps, { enabledLanguageCodes = getSelectedBrandEnabledLanguages(appProps) } = {}) {
    const languageCodes = [].concat(enabledLanguageCodes);
    languageCodes?.unshift(LANGUAGE_EN_TR);
    return languageCodes?.sort((a, b) => {
        if (a === LANGUAGE_EN) {
            return -1;
        }
        if (b === LANGUAGE_EN) {
            return 1;
        }
        if (a === LANGUAGE_EN_TR) {
            return -1;
        }
        if (b === LANGUAGE_EN_TR) {
            return 1;
        }
        return 0;
    });
}

export function getSelectedBrandCurrency(appProps) {
    return appProps?.selectedBrandData?.currency;
}

export function isBrandSelected(appProps) {
    return !!getSelectedBrandData(appProps);
}

export function isSelectedBrandRiskSystemDisabled(appProps) {
    if (isBrandSelected(appProps)) {
        return !getSelectedBrandData(appProps)?.settings?.riskSystemEnabled;
    }
    return false;
}

export function getDefaultLanguage(appProps) {
    const enabledLanguages = getSelectedBrandEnabledLanguages(appProps);
    const defaultLanguage = appProps?.selectedBrandData?.settings?.defaultLanguage;
    if (defaultLanguage && enabledLanguages.includes(defaultLanguage)) {
        return defaultLanguage;
    }
    if (enabledLanguages.includes(LANGUAGE_EN)) {
        return LANGUAGE_EN;
    }
    return enabledLanguages[0] || LANGUAGE_EN;
}

export function getLoggedInUser(appProps) {
    return appProps?.userData;
}

export function getLoggedInUserDisplayName(appProps) {
    return getLoggedInUser(appProps)?.displayName;
}

export function getUserDataUserId(props) {
    return props?.userData?.userId;
}

export function getUserDataMerchantName(props) {
    return props?.userData?.merchantName;
}

export function getUserDataShipmentCarrier(props) {
    return props?.userData?.shipmentCarrier;
}

export function convertWarehouseItemActionToComplaintStatus(warehouseItemActionStatus) {
    if (warehouseItemActionStatus === WAREHOUSE_ITEM_ACTION.WAREHOUSE_APPROVED) {
        return COMPLAINT_STATUS.APPROVED;
    }

    if (warehouseItemActionStatus === WAREHOUSE_ITEM_ACTION.WAREHOUSE_DENIED) {
        return COMPLAINT_STATUS.DENIED;
    }

    return COMPLAINT_STATUS.PENDING;
}

export function getShopAddressLine(address) {
    function addPart(part) {
        if (part) {
            return part + ", ";
        }
        return "";
    }

    const name = !address?.firstName && !address?.lastName ? "" : `${((address?.firstName || "") + " " + (address?.lastName || "")).trim()}, `;
    const text = `${name}${addPart(address?.street)}${addPart(address?.zip)}${addPart(address?.city)}${addPart(countryCodeToCountryName(address?.countryCode))}`;
    return text ? text.substring(0, text.length - 2) : text;
}

export function countryCodeToCountryGroupCountryCodes({ countryGroups, countryCode }) {
    return countryGroups?.find((pc) => pc.name === countryCode)?.memberCountries?.map((pc) => pc.countryCode);
}

export function getSelectableFonts(appProps, { fonts }) {
    return getSelectableFontsForBrand({ fonts, brandName: getSelectedBrandName(appProps) });
}

export function getComplaintActionAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.complaints;
}

export function getReturnDelaysAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.delays;
}

export function getRefundIssuesAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.refundIssues?.total;
}

export function getWarehouseIssuesAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.warehouseIssues?.total;
}

export function getShipmentIssuesAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.shipmentIssues?.total;
}

export function getUploadShipmentLabelAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.uploadShipmentLabel;
}

export function getWarehouseRemarksActionAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.warehouseRemarks;
}

export function getReturnIssuesTotalAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.returnIssues?.total;
}

export function getExchangeIssuesTotalAmount(appProps) {
    return appProps?.actionSummaryServiceData?.data?.exchangeIssues?.total;
}

export function getLoginName(appProps) {
    return getSelectedBrandName(appProps);
}

export function getSelectedBrandData(appProps) {
    return appProps?.selectedBrandData;
}

export function getSelectedBrandId(appProps) {
    return appProps?.selectedBrandId;
}

export function getSelectedBrandIngridSiteId(appProps) {
    return getSelectedBrandData(appProps)?.ingridSiteId;
}

export function getSelectedMerchantId(appProps) {
    return appProps?.selectedMerchantId;
}

export function getSelectedMerchantIngridId(appProps) {
    return getSelectedBrandData(appProps)?.ingridMerchantId;
}

export function getIssuesMap(appProps) {
    const complaintsPageData = getComplaintsPageData();

    return {
        [ROUTES.RETURNS_UPLOAD_LABEL]: {
            title: "Upload Shipment Label",
            amount: getUploadShipmentLabelAmount(appProps),
            href: getUploadShipmentLabelPageData().href,
        },
        [ROUTES.RETURNS_COMPLAINTS]: {
            title: "Complaints",
            amount: getComplaintActionAmount(appProps),
            href: getRouteHref(complaintsPageData.href, null, complaintsPageData.queryParams),
        },
        [ROUTES.WAREHOUSE_REQUIRE_ACTION]: {
            title: "Warehouse Remarks",
            amount: getWarehouseRemarksActionAmount(appProps),
            href: getWarehouseRemarksPageData().href,
        },
        [ROUTES.RETURNS_ISSUES]: {
            title: "Return Issues",
            type: "WARNING",
            amount: getReturnIssuesTotalAmount(appProps),
            href: ROUTES.RETURNS_ISSUES,
        },
        [ROUTES.RETURNS_SHIPMENT_ISSUES]: {
            title: "Shipment Issues",
            type: "WARNING",
            amount: getShipmentIssuesAmount(appProps),
            href: ROUTES.RETURNS_SHIPMENT_ISSUES,
        },
        [ROUTES.RETURNS_EXCHANGES]: {
            title: "Exchange Issues",
            type: "WARNING",
            amount: getExchangeIssuesTotalAmount(appProps),
            href: ROUTES.RETURNS_EXCHANGE_ISSUES,
        },
        [ROUTES.RETURNS_REFUND_ISSUES]: {
            title: "Refund Issues",
            type: "WARNING",
            amount: getRefundIssuesAmount(appProps),
            href: ROUTES.RETURNS_REFUND_ISSUES,
        },
        [ROUTES.WAREHOUSE_ISSUES]: {
            title: "Warehouse Issues",
            type: "WARNING",
            amount: getWarehouseIssuesAmount(appProps),
            href: ROUTES.WAREHOUSE_ISSUES,
        },
    };
}

export function getProductCategoryLabelFromId(appProps, { id }) {
    const productCategories = appProps.getProductCategoriesServiceData()?.data || [];
    const foundCategory = productCategories?.find((pc) => equalAsStringOrNumber(pc.id, id));
    return foundCategory?.label || foundCategory?.id;
}

export function getExchangePolicyEnumData(appProps, { id }) {
    const values = getConstantEnumData(appProps)?.["exchangePolicy"]?.values || {};
    if (id) {
        return values?.[id];
    }
    return values;
}

export function getExchangePolicyLabel(appProps, { id }) {
    return getExchangePolicyEnumData(appProps, { id })?.label;
}

export function getExchangePolicyDescription(appProps, { id }) {
    return getExchangePolicyEnumData(appProps, { id })?.description;
}

export function getConstantEnumData(appProps) {
    return appProps.constantEnumsServiceData?.data;
}

export function getCarriersData(appProps) {
    return getConstantEnumData(appProps)?.carriersData?.values || [];
}

export function getCarrierData(appProps, { id }) {
    return getCarriersData(appProps).find((c) => c.id === id);
}

export function getShipmentCarrierName(appProps, { id }) {
    const carrier = getCarrierData(appProps, { id });
    return carrier?.displayNameBackoffice ?? carrier?.displayName ?? id;
}

export function getCookiePolicyOptions(appProps) {
    return Object.values(getConstantEnumData(appProps)?.cookiePolicy?.values || {});
}

export function getProductImageObjectFitOptions(appProps) {
    return Object.values(getConstantEnumData(appProps)?.productImageObjectFits?.values || {});
}

export function getExchangePropertyPickerOptions(appProps) {
    return Object.values(getConstantEnumData(appProps)?.exchangePropertyPickers?.values || {});
}

export function getShopFrameBorderEffectOptions(appProps) {
    return Object.values(getConstantEnumData(appProps)?.frameBorderEffects?.values || {});
}

export function getQueryParam(appProps, { key }) {
    return appProps?.queryParams[key];
}

export function getPathParam(appProps, { key }) {
    return appProps?.pathParams[key];
}

export function isUserLoggedIn(appProps) {
    return appProps?.isUserLoggedIn;
}

export function getSelectedOrBrandMerchantData(appProps) {
    return appProps?.merchantService?.data || getSelectedBrandData(appProps)?.merchant;
}

export function isSelectedMerchantMetabaseEnabled(appProps) {
    return getSelectedOrBrandMerchantData(appProps)?.metabaseEnabled;
}

export function showMetabaseDashboard(appProps) {
    return isUserTypeIngrid(appProps) || isSelectedMerchantMetabaseEnabled(appProps);
}

export function getCurrentDateAsDateString(appProps) {
    return appProps?.currentDateClient?.toISOString()?.slice(0, 10);
}

export function getCurrentDateClient(appProps) {
    return appProps?.currentDateClient;
}

const rolledOutCustomers = ["ingrid.com"];

export function getLoginMethod(appProps, { email }) {
    const emailDomain = email?.split("@")[1];

    if (appProps?.pathname?.startsWith("/oldlogin")) {
        return LOGIN_METHODS.OLD;
    }
    if (rolledOutCustomers.includes(emailDomain)) {
        return LOGIN_METHODS.NEW;
    }
    if (isDevOrLocalEnv()) {
        return LOGIN_METHODS.NEW;
    }
    return LOGIN_METHODS.OLD;
}
